import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Season } from "@/types/models";
import { Button } from "@/components/ui/button";
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { FormDatePicker } from "@/components/ui/form-date-picker";
import { Info } from "lucide-react";
import { format, parse, isAfter, isBefore, addMonths, startOfMonth, startOfDay, endOfMonth } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatDateForForm, toISODateString } from '@/utils/dateUtils';

interface SeasonFormProps {
  season?: Season;
  onSubmit: (data: SeasonFormData) => void;
  organizationId: string;
}

// Date validation regex for MM/DD/YYYY format
const DATE_FORMAT_REGEX = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
const DATE_FORMAT = "MM/dd/yyyy";

const seasonFormSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  start_date: z.date({
    required_error: "Start date is required",
    invalid_type_error: "Invalid date",
  }),
  end_date: z.date({
    required_error: "End date is required",
    invalid_type_error: "Invalid date",
  }),
  registration_start: z.date({
    required_error: "Registration start date is required",
    invalid_type_error: "Invalid date",
  }),
  registration_end: z.date({
    required_error: "Registration end date is required",
    invalid_type_error: "Invalid date",
  }),
  registration_fee_enabled: z.boolean().default(false),
  tuition_prepay_enabled: z.boolean().default(false),
  is_active: z.boolean().default(false),
}).refine((data) => {
  return isAfter(data.end_date, data.start_date);
}, {
  message: "End date must be after start date",
  path: ["end_date"],
}).refine((data) => {
  return isAfter(data.registration_end, data.registration_start);
}, {
  message: "Registration end date must be after registration start date",
  path: ["registration_end"],
});

export type SeasonFormData = z.infer<typeof seasonFormSchema>;

// Date presets for quick selection
const datePresets = [
  {
    name: "Summer 2025",
    startDate: "06/01/2025",
    endDate: "08/31/2025",
    regStartDate: "03/01/2025", 
    regEndDate: "05/15/2025",
  },
  {
    name: "Fall 2025",
    startDate: "09/01/2025",
    endDate: "12/15/2025",
    regStartDate: "07/01/2025",
    regEndDate: "08/15/2025",
  },
  {
    name: "Spring 2026",
    startDate: "01/15/2026",
    endDate: "05/31/2026",
    regStartDate: "11/01/2025",
    regEndDate: "12/15/2025",
  },
  {
    name: "Next 3 Months",
    startDate: format(startOfMonth(new Date()), DATE_FORMAT),
    endDate: format(endOfMonth(addMonths(new Date(), 3)), DATE_FORMAT),
    regStartDate: format(startOfDay(new Date()), DATE_FORMAT),
    regEndDate: format(startOfDay(addMonths(new Date(), 1)), DATE_FORMAT),
  },
];

const SeasonForm: React.FC<SeasonFormProps> = ({ season, onSubmit, organizationId }) => {
  const form = useForm<SeasonFormData>({
    resolver: zodResolver(seasonFormSchema),
    defaultValues: season ? {
      name: season.name,
      start_date: season.start_date ? parse(formatDateForForm(season.start_date, DATE_FORMAT), DATE_FORMAT, new Date()) : startOfMonth(new Date()),
      end_date: season.end_date ? parse(formatDateForForm(season.end_date, DATE_FORMAT), DATE_FORMAT, new Date()) : endOfMonth(addMonths(new Date(), 3)),
      registration_start: season.registration_start ? parse(formatDateForForm(season.registration_start, DATE_FORMAT), DATE_FORMAT, new Date()) : startOfDay(new Date()),
      registration_end: season.registration_end ? parse(formatDateForForm(season.registration_end, DATE_FORMAT), DATE_FORMAT, new Date()) : startOfDay(addMonths(new Date(), 1)),
      registration_fee_enabled: season.registration_fee_enabled,
      tuition_prepay_enabled: season.tuition_prepay_enabled,
      is_active: season.is_active,
    } : {
      name: "",
      start_date: startOfMonth(new Date()),
      end_date: endOfMonth(addMonths(new Date(), 3)),
      registration_start: startOfDay(new Date()),
      registration_end: startOfDay(addMonths(new Date(), 1)),
      registration_fee_enabled: false,
      tuition_prepay_enabled: false,
      is_active: false,
    }
  });

  const handleFormSubmit = (data: SeasonFormData) => {
    // Convert Date objects to ISO date strings (YYYY-MM-DD) to avoid timezone issues
    const processedData = {
      ...data,
      start_date: format(data.start_date, 'yyyy-MM-dd'),
      end_date: format(data.end_date, 'yyyy-MM-dd'),
      registration_start: format(data.registration_start, 'yyyy-MM-dd'),
      registration_end: format(data.registration_end, 'yyyy-MM-dd'),
    };
    
    onSubmit(processedData as any);
  };

  const applyPreset = (preset: typeof datePresets[0]) => {
    form.setValue('start_date', parse(preset.startDate, DATE_FORMAT, new Date()));
    form.setValue('end_date', parse(preset.endDate, DATE_FORMAT, new Date()));
    form.setValue('registration_start', parse(preset.regStartDate, DATE_FORMAT, new Date()));
    form.setValue('registration_end', parse(preset.regEndDate, DATE_FORMAT, new Date()));
    if (!form.getValues('name') && preset.name !== "Next 3 Months") {
      form.setValue('name', preset.name);
    }
  };


  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-8">
        <div className="space-y-5">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base font-semibold">Season Name</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Summer 2025" 
                    {...field} 
                    className="text-base" 
                  />
                </FormControl>
                <FormDescription>
                  Choose a descriptive name like "Fall 2025" or "Summer Camp 2025"
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Separator className="my-6" />
        
        <div className="bg-slate-50 p-4 rounded-lg mb-6">
          <h3 className="text-base font-medium mb-3">Quick Date Presets</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {datePresets.map((preset, index) => (
              <Button 
                key={index} 
                variant="outline" 
                size="sm" 
                className="h-auto py-3 px-4 justify-start text-left"
                onClick={() => applyPreset(preset)}
                type="button"
              >
                <div className="w-full">
                  <div className="font-medium text-sm">{preset.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {preset.startDate} → {preset.endDate}
                  </div>
                </div>
              </Button>
            ))}
          </div>
        </div>
        
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Season Dates</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Define when this season begins and ends (MM/DD/YYYY)
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormDatePicker
              form={form}
              name="start_date"
              label="Season Start Date"
              placeholder="Select start date"
            />

            <FormDatePicker
              form={form}
              name="end_date"
              label="Season End Date"
              description="When the season's classes will end"
              placeholder="Select end date"
            />
          </div>
        </div>

        <Separator className="my-6" />

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium">Registration Window</h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">The period when students can register for classes in this season</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            When will registration be open for this season? (MM/DD/YYYY)
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormDatePicker
              form={form}
              name="registration_start"
              label="Registration Opens"
              placeholder="Select registration start"
            />

            <FormDatePicker
              form={form}
              name="registration_end"
              label="Registration Closes"
              placeholder="Select registration end"
            />
          </div>
        </div>

        <Separator className="my-6" />
        
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Season Settings</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Configure additional options for this season
          </p>

          <div className="space-y-6">
            <FormField
              control={form.control}
              name="registration_fee_enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-base">Charge Registration Fee</FormLabel>
                    <FormDescription>
                      Enable registration fees for this season
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tuition_prepay_enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-base">Collect Tuition Early</FormLabel>
                    <FormDescription>
                      Collect the first tuition payment during registration, even if the season hasn't started
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-base">Set as Current Season</FormLabel>
                    <FormDescription>
                      Mark this as your active season (will deactivate any other current season)
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white px-8">
            Save Season
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default SeasonForm;
